// Central route-aware SEO/head manager.
// On every route change it updates document.title plus the description, keywords,
// canonical, Open Graph and Twitter Card tags so metadata never leaks between routes.

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

// Brand logo — existing absolute asset already used as the site favicon in index.html.
export const BRAND_IMAGE =
  'https://storage.helloreaddy.io/project_files/0c9a9c3e-9011-4ee6-9dd2-90fcbe3bf101/f29088da-a32d-42f3-a831-1504283ab725_compressed_dk.webp';

export const SITE_NAME = 'DK Supply';

export const DEFAULT_TITLE = 'Catálogo de productos | DK Supply';

export const CATALOG_DESCRIPTION =
  'Explora nuestro catálogo de productos, consulta precios y disponibilidad, y contáctanos por WhatsApp para más información o para realizar tu pedido.';

export function getSiteOrigin(): string {
  const configured = import.meta.env.VITE_SITE_URL as string | undefined;
  if (configured) return configured.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

function catalogSeo(): SeoData {
  const origin = getSiteOrigin();
  const url = origin ? `${origin}/catalogo` : undefined;
  return {
    title: 'Catálogo de Productos',
    description: CATALOG_DESCRIPTION,
    keywords: 'catálogo de productos, tienda, precios, disponibilidad, WhatsApp',
    canonical: url,
    ogTitle: 'Catálogo de Productos',
    ogDescription: CATALOG_DESCRIPTION,
    ogType: 'website',
    ogUrl: url,
    ogImage: BRAND_IMAGE,
    twitterCard: 'summary_large_image',
    twitterTitle: 'Catálogo de Productos',
    twitterDescription: CATALOG_DESCRIPTION,
    twitterImage: BRAND_IMAGE,
  };
}

const seoByRoute: Record<string, () => SeoData> = {
  '/catalogo': catalogSeo,
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function setMeta(attr: 'name' | 'property', key: string, content?: string): void {
  if (content) upsertMeta(attr, key, content);
  else removeMeta(attr, key);
}

function setCanonical(href?: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href) {
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  } else if (el) {
    el.remove();
  }
}

export function applySeo(pathname: string): void {
  if (typeof document === 'undefined') return;

  const builder = seoByRoute[pathname];
  const seo = builder ? builder() : null;

  document.title = seo?.title ?? DEFAULT_TITLE;

  setMeta('name', 'description', seo?.description);
  setMeta('name', 'keywords', seo?.keywords);
  setCanonical(seo?.canonical);

  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:title', seo?.ogTitle);
  setMeta('property', 'og:description', seo?.ogDescription);
  setMeta('property', 'og:type', seo?.ogType);
  setMeta('property', 'og:url', seo?.ogUrl);
  setMeta('property', 'og:image', seo?.ogImage);

  setMeta('name', 'twitter:card', seo?.twitterCard);
  setMeta('name', 'twitter:title', seo?.twitterTitle);
  setMeta('name', 'twitter:description', seo?.twitterDescription);
  setMeta('name', 'twitter:image', seo?.twitterImage);
}